import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, ClipboardPaste, Loader2, Save, Upload, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

interface Props {
  condominioId: number;
  condominioNome?: string;
  onFechar: () => void;
}

interface FracaoResp {
  unidades: string[];
  fracao_ideal: Record<string, number>;
  soma: number;
}

// Resultado do backend ao importar de arquivo/texto (não grava; a tela preenche).
interface ImportResp {
  itens: Record<string, number>; // {unidade canônica: fração(0..1)}
  formato: "fracao" | "percentual";
  soma: number;
  soma_ok: boolean;
  linhas_lidas: number;
  casados: number;
  nao_casados: string[];
  sem_valor: string[];
  duplicados: string[];
  invalidos: number;
  problemas: string[];
  consistente: boolean;
  aviso: string | null;
}

type Modo = "fracao" | "percentual";

// aceita vírgula ou ponto
function parseNum(s: string): number | null {
  const t = s.trim().replace("%", "").replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// formata um número tirando zeros à direita, com vírgula
function fmtNum(n: number, casas: number): string {
  let s = n.toFixed(casas);
  if (s.includes(".")) s = s.replace(/0+$/, "").replace(/\.$/, "");
  return s.replace(".", ",");
}

// converte a FRAÇÃO guardada (0..1) para o texto exibido no modo atual
function fracaoParaTexto(fr: number, modo: Modo): string {
  return modo === "percentual" ? fmtNum(fr * 100, 6) : fmtNum(fr, 8);
}

export function FracaoIdealModal({ condominioId, condominioNome, onFechar }: Props) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<FracaoResp>({
    queryKey: ["fracao-ideal", condominioId],
    queryFn: () => api.get<FracaoResp>(`/condominios/${condominioId}/fracao-ideal`),
  });

  // Padrão: FRAÇÃO (soma = 1) — é como a Convenção costuma trazer (0,086328).
  const [modo, setModo] = useState<Modo>("fracao");
  const [vals, setVals] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);

  // Inicializa a partir do que veio do banco (uma vez, quando 'data' chega).
  useEffect(() => {
    if (!data) return;
    const init: Record<string, string> = {};
    for (const u of data.unidades) {
      const fr = data.fracao_ideal[u];
      init[u] = fr != null ? fracaoParaTexto(fr, modo) : "";
    }
    setVals(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Trocar modo CONVERTE os valores já digitados (não perde o que foi editado).
  function trocarModo(novo: Modo) {
    if (novo === modo) return;
    setVals((prev) => {
      const out: Record<string, string> = {};
      for (const [u, s] of Object.entries(prev)) {
        const n = parseNum(s);
        if (n == null) { out[u] = ""; continue; }
        // n está no modo ANTIGO → vira fração → texto no modo NOVO
        const fr = modo === "percentual" ? n / 100 : n;
        out[u] = fracaoParaTexto(fr, novo);
      }
      return out;
    });
    setModo(novo);
  }

  const alvo = modo === "percentual" ? 100 : 1;
  const tol = modo === "percentual" ? 0.05 : 0.0005;
  const soma = Object.values(vals).reduce((acc, s) => acc + (parseNum(s) ?? 0), 0);
  const somaOk = Math.abs(soma - alvo) < tol;
  const somaTxt =
    modo === "percentual" ? `${fmtNum(soma, 4)}%` : fmtNum(soma, 6);
  const alvoTxt = modo === "percentual" ? "100%" : "1";

  const salvar = useMutation({
    mutationFn: () => {
      const fracao_ideal: Record<string, number> = {};
      for (const [u, s] of Object.entries(vals)) {
        const n = parseNum(s);
        if (n != null && n > 0) fracao_ideal[u] = modo === "percentual" ? n / 100 : n;
      }
      return api.put(`/condominios/${condominioId}/fracao-ideal`, { fracao_ideal });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fracao-ideal", condominioId] });
      setMsg("Tabela de fração ideal salva.");
    },
    onError: () => setMsg("Não foi possível salvar. Tente novamente."),
  });

  // ---- Importar de arquivo (.xlsx/.csv) ou texto colado ----
  const fileRef = useRef<HTMLInputElement>(null);
  const [colarAberto, setColarAberto] = useState(false);
  const [textoColado, setTextoColado] = useState("");
  // Resultado da pré-consistência do último import (para o painel de avisos).
  const [importInfo, setImportInfo] = useState<ImportResp | null>(null);

  // Aplica o resultado do import nos campos (para revisar antes de salvar).
  // Não sobrescreve valores já digitados de unidades que não vieram no arquivo.
  function aplicarResultado(res: ImportResp) {
    if (!data) return;
    const novoModo: Modo = res.formato === "percentual" ? "percentual" : "fracao";
    setVals((prev) => {
      const next: Record<string, string> = { ...prev };
      for (const u of data.unidades) {
        const fr = res.itens[u];
        if (fr != null) next[u] = fracaoParaTexto(fr, novoModo);
      }
      return next;
    });
    setModo(novoModo);
    setMsg(null);       // o painel de importInfo assume o feedback do import
    setImportInfo(res);
  }

  const importarArquivo = useMutation({
    mutationFn: (file: File) =>
      api.upload<ImportResp>(`/condominios/${condominioId}/fracao-ideal/importar-arquivo`, file),
    onSuccess: aplicarResultado,
    onError: (e) =>
      setMsg(e instanceof Error ? e.message : "Não consegui ler o arquivo. Confira o formato."),
  });
  const importarTexto = useMutation({
    mutationFn: (texto: string) =>
      api.post<ImportResp>(`/condominios/${condominioId}/fracao-ideal/importar-texto`, { texto }),
    onSuccess: (r) => { aplicarResultado(r); setColarAberto(false); setTextoColado(""); },
    onError: (e) =>
      setMsg(e instanceof Error ? e.message : "Não consegui ler o texto colado."),
  });
  const importando = importarArquivo.isPending || importarTexto.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Tabela de Fração Ideal</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {condominioNome ? `${condominioNome} · ` : ""}
              participação de cada unidade (da Convenção).
            </p>
          </div>
          <button type="button" onClick={onFechar} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        {/* Corpo */}
        <div className="p-5 overflow-y-auto">
          {/* Escolha do formato */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs">
            <span className="text-slate-600">Informar como:</span>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={modo === "fracao"} onChange={() => trocarModo("fracao")} />
              Fração (soma = 1) — ex.: 0,086328
            </label>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={modo === "percentual"} onChange={() => trocarModo("percentual")} />
              Percentual (soma = 100%) — ex.: 8,6328
            </label>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={16} className="animate-spin" /> Carregando…
            </div>
          ) : !data || data.unidades.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Este condomínio ainda não tem unidades cadastradas. Cadastre as unidades
              (ou processe um balancete) para preencher a fração ideal.
            </div>
          ) : (
            <>
              {/* Importar de arquivo pronto (menos manual) */}
              <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xlsm,.csv,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) importarArquivo.mutate(f);
                    e.target.value = ""; // permite reimportar o mesmo arquivo
                  }}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={importando}
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {importando ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Importar arquivo (.xlsx/.csv)
                  </button>
                  <button
                    type="button"
                    disabled={importando}
                    onClick={() => setColarAberto((v) => !v)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:border-slate-400 disabled:opacity-50"
                  >
                    <ClipboardPaste size={14} /> ou colar do Excel
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Precisa ter <b>o apto e o valor</b> (fração <b>0,086328</b> ou percentual
                  <b> 8,6328</b>). O app <b>acha sozinho</b> as colunas certas e <b>ignora</b>
                  {" "}cabeçalho, coluna de nome/índice, linha de total e linhas em branco —
                  então pode usar sua planilha do jeito que está.
                </p>
                {colarAberto && (
                  <div className="mt-2">
                    <textarea
                      value={textoColado}
                      onChange={(e) => setTextoColado(e.target.value)}
                      rows={4}
                      placeholder={"101\t0,25\n102\t0,25\n201\t0,25\n202\t0,25"}
                      className="w-full px-2 py-1.5 text-xs font-mono border border-slate-300 rounded-md"
                    />
                    <div className="flex justify-end mt-1">
                      <button
                        type="button"
                        disabled={importando || textoColado.trim() === ""}
                        onClick={() => importarTexto.mutate(textoColado)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Aplicar valores colados
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Pré-consistência do import: verde se OK, vermelho listando problemas */}
              {importInfo && (
                importInfo.consistente ? (
                  <div className="mb-3 flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                    <span>
                      <b>Nenhuma inconsistência detectada.</b> {importInfo.casados} unidade(s)
                      preenchida(s) em {importInfo.formato === "percentual" ? "percentual" : "fração"}.
                      Confira os valores e clique em <b>Salvar</b>.
                    </span>
                  </div>
                ) : (
                  <div className="mb-3 rounded-lg border-2 border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                    <div className="flex items-center gap-2 font-semibold mb-1">
                      <AlertCircle size={16} className="flex-shrink-0" />
                      Verifique o arquivo de origem — encontrei:
                    </div>
                    <ul className="list-disc pl-6 space-y-0.5">
                      {importInfo.problemas.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                    <p className="mt-1.5 text-[12px] text-rose-700">
                      Os aptos que bateram já foram preenchidos abaixo. Corrija o que faltar
                      (no arquivo ou à mão) até a soma fechar e clique em <b>Salvar</b>.
                    </p>
                  </div>
                )
              )}

              <div className="grid grid-cols-2 gap-2">
                {data.unidades.map((u) => (
                  <label key={u} className="flex items-center gap-2 text-sm">
                    <span className="w-20 font-medium text-slate-800">{u}</span>
                    <div className="relative flex-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={vals[u] ?? ""}
                        onChange={(e) => setVals((p) => ({ ...p, [u]: e.target.value }))}
                        placeholder={modo === "percentual" ? "0,0000" : "0,000000"}
                        className={`w-full ${modo === "percentual" ? "pr-6" : ""} pl-2 py-1.5 text-sm text-right border border-slate-300 rounded-md tabular-nums`}
                      />
                      {modo === "percentual" && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {/* Soma corrente */}
              <div
                className={`mt-4 flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-semibold ${
                  somaOk
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : "border-rose-300 bg-rose-50 text-rose-800"
                }`}
              >
                {somaOk ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                Soma: {somaTxt}
                {somaOk ? ` (${alvoTxt} ✓)` : ` — deve dar ${alvoTxt}`}
              </div>

              <p className="mt-2 text-[11px] text-slate-500">
                Dica: use o formato da sua Convenção. Em <b>Fração</b>, o apto 201 fica
                <b> 0,086328</b> e a soma fecha em <b>1</b>. Em <b>Percentual</b>, fica
                <b> 8,6328</b> e a soma fecha em <b>100%</b>. Guardamos sempre como fração —
                trocar o formato só converte a exibição.
              </p>
            </>
          )}

          {msg && (
            <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              {msg}
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200">
          <button type="button" onClick={onFechar} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">
            Fechar
          </button>
          <button
            type="button"
            disabled={salvar.isPending || !data || data.unidades.length === 0}
            onClick={() => { setMsg(null); salvar.mutate(); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
          >
            {salvar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
